import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useDimensionValues, usePerspectiveTree } from "./useAnalyzerData";
import { describeUsageType, FILTER_FIELD_GROUPS, filterFieldByValue } from "./analyzerDimensions";

// The first field (first option of the first group) is the popover's default selection.
const DEFAULT_FIELD = FILTER_FIELD_GROUPS[0]?.options?.[0]?.value || "";

// chipValueLabel — the selected values on a chip, capped with a "+N" overflow so a
// many-value filter stays one line ("BigQuery, Cloud SQL +1"). A perspective filter
// stores node ids, not human strings, so an optional `valueLabels` map ({ value: label })
// resolves each stored value to its label when present (falling back to the usage-type
// description / raw value for flat filters, which pass no map — back-compat).
export function chipValueLabel(selectedValues, category, valueLabels) {
  const fmt = (v) => {
    if (valueLabels && valueLabels[v] != null) return valueLabels[v];
    return category === "usage_type" ? describeUsageType(v) : v;
  };
  const vals = (selectedValues || []).map(fmt);
  if (vals.length === 0) return "";
  if (vals.length <= 2) return vals.join(", ");
  return `${vals.slice(0, 2).join(", ")} +${vals.length - 2}`;
}

// displayValue — how a raw flat field value reads in the picker (usage-type units get a
// descriptive label; everything else is shown as-is).
const displayValue = (field, v) => (field === "usage_type" ? describeUsageType(v) : v);

const labelForField = (fieldValue) => filterFieldByValue(fieldValue)?.label || fieldValue;

// buildPerspectiveTreeRows — orders owner-node tree nodes DEPTH-FIRST (each parent
// immediately followed by its own subtree) so the indentation reads as a real
// hierarchy. usePerspectiveTree returns nodes sorted level-then-id (breadth-first),
// which interleaves unrelated branches at the same depth and makes indentation
// misleading; this re-threads them via parentId. `collapsed` is a Set of node ids
// whose descendants are omitted. Returns `[{ value, label, depth, hasChildren, nodeId }]`.
// Pure/no I/O.
export function buildPerspectiveTreeRows(nodes = [], collapsed = new Set()) {
  const childrenByParent = new Map();
  for (const n of nodes) {
    const p = n.parentId == null ? null : Number(n.parentId);
    if (!childrenByParent.has(p)) childrenByParent.set(p, []);
    childrenByParent.get(p).push(n);
  }
  const out = [];
  const walk = (parentKey, depth) => {
    for (const n of childrenByParent.get(parentKey) || []) {
      const nodeId = Number(n.id);
      const hasChildren = (childrenByParent.get(nodeId) || []).length > 0;
      out.push({ value: String(n.id), label: n.label, depth, hasChildren, nodeId });
      if (hasChildren && !collapsed.has(nodeId)) walk(nodeId, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

// FilterPopover — one self-contained popover to add OR edit a filter, laid out as TWO panes:
// a left "Filter by" field list (navigation: which field do I filter?) and a right value pane
// (a searchable checkbox list of that field's values, embedded directly — NOT a floating
// dropdown), over a sticky footer with the selected count + actions. This scales past the
// handful of cost-base fields to the ownership perspectives without the field selector
// competing with the value list. Edit mode (onRemove set) preseeds field/values and offers
// "Remove filter".
function FilterPopover({ initialField, initialValues, initialValueLabels, from, to, token, onApply, onRemove, onCancel }) {
  const [field, setField] = useState(initialField || DEFAULT_FIELD);
  const [selectedValues, setSelectedValues] = useState(initialValues || []);
  const [search, setSearch] = useState("");
  // collapsed — the set of perspective owner-node ids whose subtree is folded away.
  // Tree starts fully expanded (empty set); switching field resets it.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const toggleCollapse = (nodeId) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
    return next;
  });
  // Switching field clears values + search + collapse (they belong to the previous field).
  const changeField = (f) => { setField(f); setSelectedValues([]); setSearch(""); setCollapsed(new Set()); };

  // The active field's KIND drives the value source: flat fields pull distinct cost-base
  // column values; perspective fields pull the owner-node tree. Both hooks are called
  // unconditionally (rules of hooks) but each is gated by `enabled` so only the active
  // kind actually fetches, and only its data is used below.
  const activeField = filterFieldByValue(field);
  const kind = activeField?.kind || "flat";
  const dimensionCode = activeField?.dimensionCode || null;
  const isPerspective = kind === "perspective";
  const fieldLabel = activeField?.label || "value";

  const flatQuery = useDimensionValues(field, from, to, token, !isPerspective && !!field) || {};
  const treeQuery = usePerspectiveTree(isPerspective ? dimensionCode : null, token) || {};
  const isLoading = isPerspective ? !!treeQuery.isLoading : !!flatQuery.isLoading;

  // rows — the ordered, display-ready value rows: `{ value, label, depth, hasChildren, nodeId? }`.
  // Flat fields → one row per distinct column value (depth 0). Perspective fields → the
  // owner-node tree, DEPTH-FIRST and collapsible; while searching, a flat list of label
  // matches (chevrons/collapse don't apply to a filtered view, but depth is kept for context).
  const searchLower = search.trim().toLowerCase();
  const flatRows = (flatQuery.data || []).map((v) => ({ value: v, label: displayValue(field, v), depth: 0, hasChildren: false }));
  const treeNodes = treeQuery.data || [];
  let rows;
  if (isPerspective) {
    rows = searchLower
      ? treeNodes
          .filter((n) => n.label.toLowerCase().includes(searchLower))
          .map((n) => ({ value: String(n.id), label: n.label, depth: (n.level || 1) - 1, hasChildren: false, nodeId: Number(n.id) }))
      : buildPerspectiveTreeRows(treeNodes, collapsed);
  } else {
    rows = searchLower
      ? flatRows.filter((r) => r.label.toLowerCase().includes(searchLower) || String(r.value).toLowerCase().includes(searchLower))
      : flatRows;
  }

  const toggle = (v) => setSelectedValues((sel) => (sel.includes(v) ? sel.filter((x) => x !== v) : [...sel, v]));
  const visibleValues = rows.map((r) => r.value);
  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every((v) => selectedValues.includes(v));
  const toggleAllVisible = () => setSelectedValues((sel) => {
    const set = new Set(sel);
    if (allVisibleSelected) visibleValues.forEach((v) => set.delete(v));
    else visibleValues.forEach((v) => set.add(v));
    return [...set];
  });

  // On Apply, resolve each selected value to a display label so chips/edit can render
  // without refetching the tree (falls back to a preseeded label from edit mode, then the
  // raw value). Flat filters get value→display (usage-type units stay described).
  const applyNow = () => {
    // Resolve labels from the FULL node/value set (not just visible rows) so a selected
    // node that's currently collapsed or filtered out still gets its chip label.
    const labelByValue = {};
    if (isPerspective) treeNodes.forEach((n) => { labelByValue[String(n.id)] = n.label; });
    else flatRows.forEach((r) => { labelByValue[r.value] = r.label; });
    const valueLabels = {};
    selectedValues.forEach((v) => {
      valueLabels[v] = labelByValue[v] ?? (initialValueLabels && initialValueLabels[v]) ?? v;
    });
    onApply(field, selectedValues, valueLabels);
  };

  return (
    <div
      // Elevation hierarchy: page → cards → THIS popover. A stronger edge (#CBD5E1 vs the
      // lighter card border) + a deeper-but-soft shadow lift it above the white chart card,
      // without a dark/modal treatment. overflow-hidden keeps the footer inside the radius.
      // Anchored to the trigger's right edge so the wide (720px) popover opens
      // leftward and stays on-screen — the Add-filter / chips sit on the right.
      className="absolute right-0 top-full z-50 mt-2 flex w-[720px] max-w-[94vw] flex-col overflow-hidden rounded-card border border-solid border-[#cbd5e1] bg-card shadow-[0_18px_48px_rgba(15,23,42,0.16),0_4px_12px_rgba(15,23,42,0.08)]"
      style={{ maxHeight: 600 }}
    >
      <div className="shrink-0 px-4 pt-4 pb-3">
        <div className="text-[13px] font-semibold text-text">{onRemove ? "Edit filter" : "Add filter"}</div>
      </div>

      {/* Two panes: left = field navigation, right = value selection. */}
      <div className="flex min-h-0 flex-1 border-t border-solid border-card-bd">
        {/* LEFT: Filter by */}
        <div
          className="flex shrink-0 flex-col overflow-y-auto border-r border-solid border-card-bd bg-surface/40 px-2 py-2"
          style={{ width: 200 }}
        >
          <div className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">Filter by</div>
          {FILTER_FIELD_GROUPS.map((grp) => (
            <div key={grp.group} className="mb-1">
              <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-text-3">{grp.group}</div>
              {grp.options.map((o) => {
                const active = o.value === field;
                return (
                  <button
                    key={o.value}
                    type="button"
                    aria-label={o.label}
                    aria-pressed={active}
                    onClick={() => changeField(o.value)}
                    style={{ height: 38 }}
                    className={`flex w-full items-center justify-between gap-2 box-border rounded-[10px] px-2.5 text-left text-[14px] transition-colors ${
                      active ? "bg-primary-soft font-semibold text-text" : "font-medium text-text-2 hover:bg-surface-2 hover:text-text"
                    }`}
                  >
                    <span className="truncate">{o.label}</span>
                    {active && <FontAwesomeIcon icon={icon({ name: "check", style: "solid" })} className="shrink-0 text-[11px] text-primary" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* RIGHT: Value */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="shrink-0 px-4 pt-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">Value</div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${fieldLabel} values…`}
              aria-label={`Search ${fieldLabel} values`}
              style={{ width: "100%", height: 40 }}
              className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 text-[14px] text-text focus:border-primary focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] focus:outline-none"
            />
            {isPerspective && (
              <div className="mt-1.5 text-[12px] text-text-3">Selecting a group includes everything under it.</div>
            )}
            {rows.length > 8 && (
              <div className="mt-2 text-[12px]">
                <button type="button" onClick={toggleAllVisible} className="text-primary hover:underline">
                  {allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                </button>
              </div>
            )}
          </div>

          <div className="mt-2 min-h-0 flex-1 overflow-auto px-2">
            {isLoading ? (
              <div className="px-2 py-2 text-[13px] text-text-3">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="px-2 py-2 text-[13px] text-text-3">No values found</div>
            ) : (
              rows.map((r) => {
                const checked = selectedValues.includes(r.value);
                const isCollapsed = r.nodeId != null && collapsed.has(r.nodeId);
                // The selectable row: checkbox + label. Parent (group) rows read slightly
                // stronger than leaf rows. width via flex-1 so it fills the row.
                const option = (
                  <button
                    type="button"
                    role="option"
                    aria-selected={checked}
                    onClick={() => toggle(r.value)}
                    style={{ height: 38 }}
                    className={`flex min-w-0 flex-1 items-center gap-2.5 box-border rounded-ctrl px-2 text-left text-[14px] transition-colors ${
                      checked ? "bg-primary-soft text-primary-hover" : "text-text-2 hover:bg-surface-2 hover:text-text"
                    } ${isPerspective && r.hasChildren ? "font-medium text-text" : ""}`}
                  >
                    <span
                      style={{ width: 16, height: 16 }}
                      className={`inline-flex shrink-0 items-center justify-center rounded-sm border border-solid box-border ${
                        checked ? "border-primary bg-primary text-white" : "border-card-bd bg-card"
                      }`}
                    >
                      {checked && <FontAwesomeIcon icon={icon({ name: "check", style: "solid" })} className="text-[9px]" />}
                    </span>
                    <span className="truncate">{r.label}</span>
                  </button>
                );
                // Flat fields render the row as-is. Perspective fields prepend a depth
                // indent + a fixed toggle column (chevron for a group, blank for a leaf) so
                // checkboxes align per depth and parents can be collapsed. The theme
                // overrides Tailwind's w-*/pl-* scales, so sizes are inline.
                if (!isPerspective) return <div key={r.value} className="flex">{option}</div>;
                return (
                  <div key={r.value} className="flex items-center">
                    <span aria-hidden style={{ width: 8 + r.depth * 18 }} className="shrink-0" />
                    {r.hasChildren ? (
                      <button
                        type="button"
                        aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${r.label}`}
                        onClick={(e) => { e.stopPropagation(); toggleCollapse(r.nodeId); }}
                        style={{ width: 20, height: 38 }}
                        className="inline-flex shrink-0 items-center justify-center text-text-3 transition-colors hover:text-text"
                      >
                        <FontAwesomeIcon icon={icon({ name: "chevron-right", style: "solid" })} className={`text-[10px] transition-transform ${isCollapsed ? "" : "rotate-90"}`} />
                      </button>
                    ) : (
                      <span aria-hidden style={{ width: 20 }} className="shrink-0" />
                    )}
                    {option}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <div className="shrink-0 flex items-center justify-between gap-2 border-t border-solid border-card-bd bg-card px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-[12px] tabular-nums text-text-3">{selectedValues.length} selected</span>
          {onRemove && (
            <button type="button" onClick={onRemove} className="text-[13px] text-danger hover:underline">Remove filter</button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {selectedValues.length > 0 && (
            <button type="button" onClick={() => setSelectedValues([])} className="text-[13px] text-text-3 transition-colors hover:text-text hover:underline">Clear</button>
          )}
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-1.5 text-[13px] text-text-2 shadow-sm transition-colors hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={applyNow}
            disabled={!field || selectedValues.length === 0}
            className="inline-flex items-center box-border rounded-ctrl border border-solid border-primary bg-primary px-3 py-1.5 text-[13px] font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

// AnalyzerFilterBar — the compact "what data am I looking at?" scope row: a filter icon,
// the applied filters as chips (click body to edit, × to remove), a quiet "＋ Add filter"
// that opens a popover, and "Clear all" once there are ≥2 filters. No big bordered
// container — the chips carry the state. Filters use the same store shape as drillInto.
export default function AnalyzerFilterBar({ filters = [], setFilters, token, from, to, controls = null, trailing = null }) {
  // editing: null | "add" | <index>
  const [editing, setEditing] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (editing === null) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setEditing(null); };
    const onKey = (e) => { if (e.key === "Escape") setEditing(null); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [editing]);

  // buildFilter carries kind + dimensionCode + valueLabels so downstream consumers
  // (owner-scoped requests, chips, edit-mode preseed) know a perspective filter from a
  // flat one, and can render its node ids as labels without refetching the tree.
  const buildFilter = (fieldValue, selectedValues, valueLabels) => {
    const opt = filterFieldByValue(fieldValue);
    return {
      category: fieldValue,
      categoryDisplay: opt?.label || labelForField(fieldValue),
      kind: opt?.kind || "flat",
      dimensionCode: opt?.dimensionCode || null,
      label: "",
      values: [],
      selectedValues,
      valueLabels: valueLabels || {},
      key: fieldValue,
    };
  };

  const applyAdd = (fieldValue, selectedValues, valueLabels) => {
    const next = buildFilter(fieldValue, selectedValues, valueLabels);
    const exists = filters.find((f) => f.category === fieldValue);
    setFilters(exists ? filters.map((f) => (f.category === fieldValue ? next : f)) : [...filters, next]);
    setEditing(null);
  };

  const applyEdit = (index) => (fieldValue, selectedValues, valueLabels) => {
    const next = buildFilter(fieldValue, selectedValues, valueLabels);
    // Editing to a field that already exists elsewhere folds into that one filter.
    const deduped = filters.filter((f, i) => i !== index && f.category !== fieldValue);
    const rebuilt = [];
    filters.forEach((f, i) => {
      if (i === index) rebuilt.push(next);
      else if (deduped.includes(f)) rebuilt.push(f);
    });
    setFilters(rebuilt);
    setEditing(null);
  };

  const removeAt = (index) => setFilters(filters.filter((_, i) => i !== index));
  const clearAll = () => { setFilters([]); setEditing(null); };

  // Quiet outline button — a filter icon + label, deliberately low-emphasis so it
  // doesn't compete with the analytical controls; applied chips sit right after it.
  const addBtn = useMemo(() => (
    <button
      type="button"
      onClick={() => setEditing(editing === "add" ? null : "add")}
      style={{ height: 32 }}
      className="inline-flex items-center gap-1.5 box-border cursor-pointer rounded-ctrl border border-solid border-card-bd bg-surface px-3 text-[13px] font-medium text-text-2 transition-colors hover:bg-side hover:text-text"
    >
      <FontAwesomeIcon icon={icon({ name: "filters", family: "sharp", style: "light" })} className="text-[12px] text-text-3" />
      Add filter
    </button>
  ), [editing]);

  return (
    <div ref={ref} className="flex w-full flex-wrap items-center gap-x-4 gap-y-2.5">
      {/* Left: the page's view controls (Breakdown, Basis) — stay on the top row. */}
      <div className="flex items-center gap-4">{controls}</div>

      {/* API-equivalent toggle — pushed right, stays on the top row. */}
      {trailing && <div className="ml-auto">{trailing}</div>}

      {/* Filter group: the quiet Add-filter button + applied chips, kept together
          as one unit. When it doesn't fit beside the controls it wraps to a new
          line, left-aligned; the controls above it don't move. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative inline-flex">
          {addBtn}
          {editing === "add" && (
            <FilterPopover
              from={from}
              to={to}
              token={token}
              onApply={applyAdd}
              onCancel={() => setEditing(null)}
            />
          )}
        </div>
        {filters.map((f, i) => (
          <div key={f.key || f.category || i} className="relative inline-flex">
            <span
              className="inline-flex items-center overflow-hidden rounded-full border border-solid border-primary-soft-strong bg-primary-soft"
              style={{ height: 32 }}
            >
              <button
                type="button"
                onClick={() => setEditing(editing === i ? null : i)}
                className="inline-flex items-center gap-1 box-border cursor-pointer border-0 bg-transparent pl-3 pr-1.5 text-[13px] text-text-2 transition-colors hover:text-text"
                aria-label={`Edit ${f.categoryDisplay} filter`}
              >
                <span className="text-text-3">{f.categoryDisplay}:</span>
                <span className="max-w-[220px] truncate font-semibold text-primary">{chipValueLabel(f.selectedValues, f.category, f.valueLabels)}</span>
              </button>
              <button
                type="button"
                onClick={() => removeAt(i)}
                aria-label={`Remove ${f.categoryDisplay} filter`}
                className="inline-flex h-full items-center box-border cursor-pointer border-0 bg-transparent pl-1 pr-2.5 text-[13px] text-text-3 transition-colors hover:text-text"
              >
                ×
              </button>
            </span>
            {editing === i && (
              <FilterPopover
                initialField={f.category}
                initialValues={f.selectedValues || []}
                initialValueLabels={f.valueLabels || {}}
                from={from}
                to={to}
                token={token}
                onApply={applyEdit(i)}
                onRemove={() => { removeAt(i); setEditing(null); }}
                onCancel={() => setEditing(null)}
              />
            )}
          </div>
        ))}
        {filters.length >= 2 && (
          <button
            type="button"
            onClick={clearAll}
            className="ml-1 text-[12.5px] font-semibold text-primary transition-colors hover:underline"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
